import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import * as THREE from 'three';

// Inline simple OrbitControls to avoid import path issues
function createOrbitControls(camera: THREE.Camera, domElement: HTMLElement, onInteractStart?: () => void, onInteractEnd?: () => void) {
  let isDown = false;
  let prevX = 0, prevY = 0;
  let spherical = { theta: Math.PI / 4, phi: Math.PI / 2.5, radius: 14 };
  const target = new THREE.Vector3(0, 0.5, 0);
  const minPhi = 0.1, maxPhi = Math.PI / 2 - 0.05;
  const minDist = 5, maxDist = 18;

  function update() {
    spherical.phi = Math.max(minPhi, Math.min(maxPhi, spherical.phi));
    spherical.radius = Math.max(minDist, Math.min(maxDist, spherical.radius));
    const x = target.x + spherical.radius * Math.sin(spherical.phi) * Math.cos(spherical.theta);
    const y = target.y + spherical.radius * Math.cos(spherical.phi);
    const z = target.z + spherical.radius * Math.sin(spherical.phi) * Math.sin(spherical.theta);
    camera.position.set(x, y, z);
    (camera as THREE.PerspectiveCamera).lookAt(target);
  }

  const onPointerDown = (e: PointerEvent) => {
    isDown = true; prevX = e.clientX; prevY = e.clientY;
    domElement.setPointerCapture(e.pointerId);
    onInteractStart?.();
  };
  const onPointerUp = (e: PointerEvent) => {
    isDown = false;
    domElement.releasePointerCapture(e.pointerId);
    onInteractEnd?.();
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!isDown) return;
    const dx = e.clientX - prevX;
    const dy = e.clientY - prevY;
    // Wens Franky (19/8): natuurlijke draairichting — vinger/muis naar links
    // sleept het huis mee naar links (was voorheen omgekeerd).
    spherical.theta += dx * 0.008;
    spherical.phi -= dy * 0.008;
    prevX = e.clientX; prevY = e.clientY;
    update();
  };
  const onWheel = (e: WheelEvent) => { e.preventDefault(); spherical.radius += e.deltaY * 0.01; update(); };
  let lastTouchDist = 0;
  const onTouchStart = (e: TouchEvent) => {
    onInteractStart?.();
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastTouchDist = Math.sqrt(dx * dx + dy * dy);
    }
  };
  const onTouchEnd = () => {
    onInteractEnd?.();
  };
  const onTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      spherical.radius += (lastTouchDist - dist) * 0.03;
      lastTouchDist = dist;
      update();
    }
  };

  domElement.addEventListener('pointerdown', onPointerDown);
  domElement.addEventListener('pointerup', onPointerUp);
  domElement.addEventListener('pointermove', onPointerMove);
  domElement.addEventListener('wheel', onWheel, { passive: false });
  domElement.addEventListener('touchstart', onTouchStart, { passive: true });
  domElement.addEventListener('touchend', onTouchEnd, { passive: true });
  domElement.addEventListener('touchmove', onTouchMove, { passive: true });

  update();

  return {
    update,
    autoRotateTick(deltaTheta: number) {
      spherical.theta += deltaTheta;
      update();
    },
    // V6: vloeiend naar een gevelzijde draaien (klik op een nummerbolletje)
    draaiNaar(doelTheta: number) {
      let delta = doelTheta - (spherical.theta % (Math.PI * 2));
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      const start = spherical.theta, startT = performance.now(), duur = 650;
      const stap = (nu: number) => {
        const f = Math.min(1, (nu - startT) / duur);
        const e = 1 - Math.pow(1 - f, 3); // ease-out
        spherical.theta = start + delta * e;
        update();
        if (f < 1) requestAnimationFrame(stap);
      };
      requestAnimationFrame(stap);
    },
    dispose() {
      domElement.removeEventListener('pointerdown', onPointerDown);
      domElement.removeEventListener('pointerup', onPointerUp);
      domElement.removeEventListener('pointermove', onPointerMove);
      domElement.removeEventListener('wheel', onWheel);
      domElement.removeEventListener('touchstart', onTouchStart);
      domElement.removeEventListener('touchend', onTouchEnd);
      domElement.removeEventListener('touchmove', onTouchMove);
    }
  };
}

export interface CalcData {
  productName: string;
  productColor: string;
  productTexture: string;
  pricePerM2: number;
  area: number;
  jointWidth: number;
  cornerCount: number;
  cornerLength: number;
  cuttingLoss: number;
  includeGlue: boolean;
  includeGrout: boolean;
  groutColor: string;
  totalStones: number;
  stonesPerM2: number;
  totalCornerPieces: number;
  glueKg: number;
  glueBags: number;
  groutKg: number;
  groutBags: number;
  stonePrice: number;
  cornerPrice: number;
  gluePrice: number;
  groutPrice: number;
  totalPrice: number;
}

interface HousePreview3DProps {
  brickColor: string;
  /** V6: gekozen offerte-stenen (kleur + voegbreedte) — elk krijgt zijn eigen gevelvlak. */
  stenen?: GevelSteen[];
  labels: {
    title: string;
    rotate: string;
    download: string;
    uploadTeaser: string;
    uploadCta: string;
    roofLabel: string;
    roofFlat: string;
    roofTiles: string;
    roofSlate: string;
    nightMode: string;
    dayMode?: string;
    dragHint?: string;
    roofToggle?: string;
    roofToggleClose?: string;
    materialsLabel?: string;
    materialsWall?: string;
    materialsWindows?: string;
    materialsNet?: string;
    gardenClick?: string;
  };
  calcData?: CalcData;
}

// Procedural brick texture
// V6 (vraag Franky 19/8): de gekozen voegbreedte is nu ook zichtbaar in de
// 3D-textuur — 0 mm = stenen strak tegen elkaar, 15 mm = duidelijk brede voeg.
function createBrickTexture(hexColor: string, w = 512, h = 512, voegMm = 10): HTMLCanvasElement {
  const c = document.createElement('canvas'); c.width = w; c.height = h;
  const ctx = c.getContext('2d')!;
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  ctx.fillStyle = '#d4cfc8'; ctx.fillRect(0, 0, w, h);
  // schaal: echte steen ±215 mm breed op 60 px -> 1 mm ≈ 0,28 px, x2 voor leesbaarheid
  const bW = 60, bH = 22, gW = Math.round(voegMm * 0.56);
  const rows = Math.ceil(h / (bH + gW)), cols = Math.ceil(w / (bW + gW)) + 1;
  for (let row = 0; row < rows; row++) {
    const off = row % 2 === 0 ? 0 : (bW + gW) / 2;
    for (let col = -1; col < cols; col++) {
      const x = col * (bW + gW) + off, y = row * (bH + gW);
      const v = (Math.random() - 0.5) * 25;
      ctx.fillStyle = `rgb(${Math.max(0, Math.min(255, r + v))},${Math.max(0, Math.min(255, g + v * 0.7))},${Math.max(0, Math.min(255, b + v * 0.5))})`;
      ctx.fillRect(x, y, bW, bH);
      ctx.fillStyle = 'rgba(0,0,0,0.06)';
      ctx.fillRect(x, y + bH - 2, bW, 2);
      ctx.fillRect(x + bW - 2, y, 2, bH);
    }
  }
  return c;
}

// Roof texture
function createRoofTexture(type: string): HTMLCanvasElement {
  const c = document.createElement('canvas'); c.width = 256; c.height = 256;
  const ctx = c.getContext('2d')!;
  if (type === 'tiles') {
    ctx.fillStyle = '#8b4513'; ctx.fillRect(0, 0, 256, 256);
    for (let row = 0; row < 16; row++) {
      const off = row % 2 === 0 ? 0 : 10;
      for (let col = -1; col < 14; col++) {
        const x = col * 20 + off, y = row * 16;
        const v = (Math.random() - 0.5) * 20;
        ctx.fillStyle = `rgb(${139 + v},${69 + v * 0.5},${19 + v * 0.3})`;
        ctx.beginPath(); ctx.arc(x + 10, y + 8, 10, 0, Math.PI, false); ctx.fill();
      }
    }
  } else if (type === 'slate') {
    ctx.fillStyle = '#3a3a40'; ctx.fillRect(0, 0, 256, 256);
    for (let row = 0; row < 20; row++) {
      for (let col = 0; col < 12; col++) {
        const x = col * 22 + (row % 2 === 0 ? 0 : 11), y = row * 13;
        const v = (Math.random() - 0.5) * 15;
        ctx.fillStyle = `rgb(${58 + v},${58 + v},${64 + v})`;
        ctx.fillRect(x, y, 20, 11);
        ctx.strokeStyle = 'rgba(0,0,0,0.2)'; ctx.strokeRect(x, y, 20, 11);
      }
    }
  } else {
    ctx.fillStyle = '#4a4a4a'; ctx.fillRect(0, 0, 256, 256);
    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    for (let i = 0; i < 256; i += 8) { ctx.fillRect(0, i, 256, 1); }
  }
  return c;
}

// Floor texture (wood parquet)
function createFloorTexture(): HTMLCanvasElement {
  const c = document.createElement('canvas'); c.width = 256; c.height = 256;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#c4a87a'; ctx.fillRect(0, 0, 256, 256);
  for (let row = 0; row < 16; row++) {
    const off = row % 2 === 0 ? 0 : 32;
    for (let col = -1; col < 5; col++) {
      const x = col * 64 + off, y = row * 16;
      const v = (Math.random() - 0.5) * 15;
      ctx.fillStyle = `rgb(${196 + v},${168 + v * 0.8},${122 + v * 0.5})`;
      ctx.fillRect(x, y, 62, 14);
      ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.strokeRect(x, y, 62, 14);
    }
  }
  return c;
}

function disposeMesh(obj: THREE.Object3D) {
  obj.traverse((child) => {
    if ((child as THREE.Mesh).geometry) (child as THREE.Mesh).geometry.dispose();
    if ((child as THREE.Mesh).material) {
      const mat = (child as THREE.Mesh).material;
      if (Array.isArray(mat)) mat.forEach(m => { if (m.map) m.map.dispose(); m.dispose(); });
      else { if ((mat as THREE.MeshStandardMaterial).map) (mat as THREE.MeshStandardMaterial).map!.dispose(); (mat as THREE.Material).dispose(); }
    }
  });
}

interface HouseGroups {
  nummerSprites: THREE.Sprite[];
  roofGroup: THREE.Group;
  interiorGroup: THREE.Group;
  gardenGroup: THREE.Group;
  lightsGroup: THREE.Group;
  leftRoofPivot: THREE.Group | null;
  rightRoofPivot: THREE.Group | null;
  roofBaseAngle: number;
  isFlat: boolean;
}

export interface GevelSteen { kleur: string; voegMm: number }

function buildHouse(scene: THREE.Scene, brickColor: string, roofType: string, nightMode: boolean, roofOpen: boolean, stenen?: GevelSteen[]): HouseGroups {
  // Clear scene
  const toRemove = [...scene.children];
  toRemove.forEach(c => { scene.remove(c); disposeMesh(c); });

  // Groups
  const roofGroup = new THREE.Group(); roofGroup.name = 'roof';
  const interiorGroup = new THREE.Group(); interiorGroup.name = 'interior';
  const gardenGroup = new THREE.Group(); gardenGroup.name = 'garden';
  const lightsGroup = new THREE.Group(); lightsGroup.name = 'lights';

  // Background & fog — night is 70-80% darker, not pitch black
  const bgColor = nightMode ? 0x1a2a3d : 0xeae6e0;
  scene.background = new THREE.Color(bgColor);
  scene.fog = new THREE.Fog(bgColor, 14, 25);

  // ──── Lights ────
  const ambient = new THREE.AmbientLight(nightMode ? 0x4466aa : 0xffffff, nightMode ? 0.45 : 0.95);
  lightsGroup.add(ambient);
  // v3.2.77 — Filip's wens: 'kan je de schaduw van 1 kant laten schijnen
  // en meedraaien alsof de zon van 1 kant schijnt'. Zon staat op een
  // vaste wereldpositie (6, 10, 5) — schaduw blijft in de wereld vast,
  // en doordat de gebruiker met OrbitControls rond het huis draait LIJKT
  // de schaduw mee te draaien. Ophogen van shadow-kwaliteit voor scherpere
  // randen + frustum afgestemd op het huis zodat hij niet wegclipt.
  // v3.2.79 — Filip: 'schaduw draait mee idd maar komt niet precies van
  // 1 kant'. De zon stond hoog (52° elevatie) → korte schaduwen. Naar
  // beneden gehaald (~30° elevatie, namiddag-zon) voor langere, duide-
  // lijker zichtbare schaduwen die onmiskenbaar uit één richting komen.
  const sun = new THREE.DirectionalLight(nightMode ? 0x7799cc : 0xfff5e0, nightMode ? 0.5 : 0.85);
  sun.position.set(9, 6, 4); sun.castShadow = true;
  sun.shadow.mapSize.width = 2048; sun.shadow.mapSize.height = 2048;
  // Frustum afgestemd op huisgrootte zodat de schaduw scherp blijft.
  sun.shadow.camera.near = 0.5;
  sun.shadow.camera.far = 30;
  sun.shadow.camera.left = -10;
  sun.shadow.camera.right = 10;
  sun.shadow.camera.top = 10;
  sun.shadow.camera.bottom = -10;
  // Voorkomt shadow-acne (donkere strepen op verlichte oppervlakken).
  sun.shadow.bias = -0.0005;
  sun.shadow.normalBias = 0.02;
  lightsGroup.add(sun);
  const fill = new THREE.DirectionalLight(nightMode ? 0x445577 : 0xc4d8ff, nightMode ? 0.2 : 0.45);
  fill.position.set(-5, 4, -4);
  lightsGroup.add(fill);

  if (nightMode) {
    const warm1 = new THREE.PointLight(0xffaa44, 1.5, 7);
    warm1.position.set(-1.55, 1.5, 2.5); lightsGroup.add(warm1);
    const warm2 = new THREE.PointLight(0xffaa44, 1.5, 7);
    warm2.position.set(1.55, 1.5, 2.5); lightsGroup.add(warm2);
    const porch = new THREE.PointLight(0xffddaa, 1.0, 5);
    porch.position.set(0, 2.8, 2.3); lightsGroup.add(porch);
    const moon = new THREE.DirectionalLight(0x8899cc, 0.35);
    moon.position.set(-8, 12, -6); lightsGroup.add(moon);
    // Interior lights visible when roof open
    const intLight1 = new THREE.PointLight(0xffeedd, 0.8, 5);
    intLight1.position.set(-1.5, 2.0, 0); lightsGroup.add(intLight1);
    const intLight2 = new THREE.PointLight(0xffeedd, 0.6, 5);
    intLight2.position.set(1.5, 2.0, -1); lightsGroup.add(intLight2);
  }
  scene.add(lightsGroup);

  // ──── Materials ────
  // V6: elke gekozen steen krijgt zijn eigen gevelvlak (voor / zijkanten /
  // achter wisselen af over de offerte-stenen), elk met eigen voegbreedte.
  const steenLijst: GevelSteen[] = (stenen && stenen.length > 0)
    ? stenen : [{ kleur: brickColor, voegMm: 10 }];
  const steenTex = steenLijst.map(st => {
    const tex = new THREE.CanvasTexture(createBrickTexture(st.kleur, 512, 512, st.voegMm));
    tex.wrapS = THREE.RepeatWrapping; tex.wrapT = THREE.RepeatWrapping;
    return tex;
  });
  const steenVoorVlak = (vlak: number) => steenTex[vlak % steenTex.length];
  const brickTex = steenTex[0];
  const brickMat = new THREE.MeshStandardMaterial({ map: brickTex, roughness: 0.85, metalness: 0.02 });

  const roofCanvas = createRoofTexture(roofType);
  const roofTex = new THREE.CanvasTexture(roofCanvas);
  roofTex.wrapS = THREE.RepeatWrapping; roofTex.wrapT = THREE.RepeatWrapping;
  roofTex.repeat.set(3, 3);
  const roofMat = new THREE.MeshStandardMaterial({ map: roofTex, roughness: 0.7, metalness: 0.05 });

  const glassMat = new THREE.MeshStandardMaterial({
    color: nightMode ? 0xffcc66 : 0x88bbdd,
    roughness: nightMode ? 0.3 : 0.05,
    metalness: nightMode ? 0.2 : 0.5,
    transparent: true,
    opacity: nightMode ? 0.9 : 0.7,
    emissive: nightMode ? 0xffaa33 : 0x2255aa,
    emissiveIntensity: nightMode ? 0.7 : 0.08,
    envMapIntensity: 1.5,
    side: THREE.DoubleSide,
  });
  const frameMat = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.3, metalness: 0.6 });
  const sillMat = new THREE.MeshStandardMaterial({ color: 0xeeeeee, roughness: 0.5, metalness: 0.15 });
  const concreteMat = new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.9 });

  // Floor texture
  const floorCanvas = createFloorTexture();
  const floorTex = new THREE.CanvasTexture(floorCanvas);
  floorTex.wrapS = THREE.RepeatWrapping; floorTex.wrapT = THREE.RepeatWrapping;
  floorTex.repeat.set(4, 3);
  const floorMat = new THREE.MeshStandardMaterial({ map: floorTex, roughness: 0.7 });
  const intWallMat = new THREE.MeshStandardMaterial({ color: 0xf5f0ea, roughness: 0.9 });
  const kitchenMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.4, metalness: 0.3 });
  const sofaMat = new THREE.MeshStandardMaterial({ color: 0x5a7a8a, roughness: 0.8 });
  const tableMat = new THREE.MeshStandardMaterial({ color: 0x8b6914, roughness: 0.6 });

  // ──── Ground ────
  const groundGeo = new THREE.PlaneGeometry(16, 16);
  const groundMat = new THREE.MeshStandardMaterial({ color: nightMode ? 0x1e3a20 : 0x4a7a3a, roughness: 1 });
  const ground = new THREE.Mesh(groundGeo, groundMat);
  ground.rotation.x = -Math.PI / 2; ground.position.y = -0.01; ground.receiveShadow = true;
  scene.add(ground);

  // Driveway
  const driveGeo = new THREE.PlaneGeometry(3.5, 7);
  const driveMat = new THREE.MeshStandardMaterial({ color: nightMode ? 0x3a3a3a : 0x999999, roughness: 0.95 });
  const drive = new THREE.Mesh(driveGeo, driveMat);
  drive.rotation.x = -Math.PI / 2; drive.position.set(3.2, 0.005, 3.5); drive.receiveShadow = true;
  scene.add(drive);

  // ──── Base / foundation ────
  const base = new THREE.Mesh(new THREE.BoxGeometry(7.2, 0.18, 5.8), concreteMat);
  base.position.y = 0.09; scene.add(base);

  // ──── MAIN BUILDING ────
  const mW = 6.8, mH = 3.2, mD = 5.4; // mH reduced from 3.6 → 3.2 for better roof fit
  const wallThickness = 0.16;
  const fy = mH / 2 + 0.18;

  const makeWall = (w: number, h: number, d: number, x: number, y: number, z: number, texRepeatX = 3, texRepeatY = 2, vlak = 0) => {
    const t = steenVoorVlak(vlak).clone(); t.repeat.set(texRepeatX, texRepeatY);
    t.needsUpdate = true;
    const m = brickMat.clone(); m.map = t;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
    mesh.position.set(x, y, z); mesh.castShadow = true; mesh.receiveShadow = true;
    scene.add(mesh);
    return mesh;
  };

  // V6 (vraag Franky): nummerbolletjes per gevelvlak zodat de klant ziet
  // wélke gekozen steen waar zit — 1 = voorgevel, dan linksom (2 = linker
  // zijgevel, 3 = rechterkant/garage, 4 = achtergevel). Alleen tonen bij
  // meer dan één steen.
  const nummerSprites: THREE.Sprite[] = [];
  if (steenLijst.length > 1) {
    const maakNummer = (nr: number, x: number, y: number, z: number, vlak: number) => {
      const cv = document.createElement('canvas'); cv.width = 96; cv.height = 96;
      const cx = cv.getContext('2d')!;
      cx.beginPath(); cx.arc(48, 48, 42, 0, Math.PI * 2);
      cx.fillStyle = '#c47a3a'; cx.fill();
      cx.lineWidth = 6; cx.strokeStyle = '#fff'; cx.stroke();
      cx.fillStyle = '#fff'; cx.font = 'bold 52px sans-serif';
      cx.textAlign = 'center'; cx.textBaseline = 'middle';
      cx.fillText(String(nr), 48, 51);
      const tex = new THREE.CanvasTexture(cv);
      tex.colorSpace = THREE.SRGBColorSpace;
      const mat = new THREE.SpriteMaterial({ map: tex, depthTest: false, fog: false });
      mat.toneMapped = false; // vol oranje, niet uitgewassen door tone-mapping of mist
      const sprite = new THREE.Sprite(mat);
      sprite.scale.set(0.55, 0.55, 1);
      sprite.position.set(x, y, z);
      sprite.renderOrder = 999;
      sprite.userData.gevelVlak = vlak;
      nummerSprites.push(sprite);
      scene.add(sprite);
    };
    const vlakPos: Array<[number, number, number]> = [
      [0, fy + 0.4, mD / 2 + 0.35],       // vlak 0: voorgevel
      [-mW / 2 - 0.35, fy + 0.4, 0],      // vlak 1: linker zijgevel
      [mW / 2 + 0.35, fy + 0.4, -mD * 0.3], // vlak 2: rechterkant/garage
      [0, fy + 0.4, -mD / 2 - 0.35],      // vlak 3: achtergevel
    ];
    vlakPos.forEach(([x, y, z], vlak) => {
      maakNummer((vlak % steenLijst.length) + 1, x, y, z, vlak);
    });
  }

  // Back wall
  makeWall(mW, mH, wallThickness, 0, fy, -mD / 2, 4, 2, 3);
  // Left wall
  makeWall(wallThickness, mH, mD, -mW / 2, fy, 0, 3, 2, 1);
  // Right wall (partial - garage opening)
  makeWall(wallThickness, mH, mD * 0.4, mW / 2, fy, -mD * 0.3, 1.5, 2, 2);
  makeWall(wallThickness, mH * 0.3, mD * 0.6, mW / 2, mH * 0.85 + 0.18, mD * 0.2, 2, 0.7, 2);

  // Front wall — segments around windows and door
  makeWall(0.9, mH, wallThickness, -mW / 2 + 0.45, fy, mD / 2, 0.5, 2, 0);
  makeWall(0.45, mH, wallThickness, -1.1, fy, mD / 2, 0.3, 2, 0);
  makeWall(0.45, mH, wallThickness, 1.1, fy, mD / 2, 0.3, 2, 0);
  makeWall(0.9, mH, wallThickness, mW / 2 - 0.45, fy, mD / 2, 0.5, 2, 0);
  makeWall(mW - 1.8, 0.55, wallThickness, 0, mH - 0.275 + 0.18, mD / 2, 3, 0.3, 0);
  makeWall(1.35, 0.8, wallThickness, -1.95, 0.58, mD / 2, 0.8, 0.5, 0);
  makeWall(1.35, 0.8, wallThickness, 1.95, 0.58, mD / 2, 0.8, 0.5, 0);

  // ──── Enhanced Windows with double frames, sills, and lintels ────
  const makeDetailedWindow = (cx: number, cy: number, cz: number, ww: number, wh: number, vertical: boolean = false) => {
    const g = new THREE.Group();

    // Glass pane
    const glassGeo = vertical
      ? new THREE.BoxGeometry(0.05, wh, ww)
      : new THREE.BoxGeometry(ww, wh, 0.05);
    const glass = new THREE.Mesh(glassGeo, glassMat);
    g.add(glass);

    // Outer frame (thick, high contrast)
    const outerThick = 0.07;
    const ohw = ww / 2 + outerThick, ohh = wh / 2 + outerThick;
    if (!vertical) {
      // Top
      g.add(new THREE.Mesh(new THREE.BoxGeometry(ww + outerThick * 2, outerThick, 0.08), frameMat).translateY(wh / 2 + outerThick / 2));
      // Bottom
      g.add(new THREE.Mesh(new THREE.BoxGeometry(ww + outerThick * 2, outerThick, 0.08), frameMat).translateY(-wh / 2 - outerThick / 2));
      // Left
      g.add(new THREE.Mesh(new THREE.BoxGeometry(outerThick, wh + outerThick * 2, 0.08), frameMat).translateX(-ww / 2 - outerThick / 2));
      // Right
      g.add(new THREE.Mesh(new THREE.BoxGeometry(outerThick, wh + outerThick * 2, 0.08), frameMat).translateX(ww / 2 + outerThick / 2));
      // Cross bars (mullion)
      g.add(new THREE.Mesh(new THREE.BoxGeometry(0.03, wh, 0.07), frameMat)); // vertical center
      g.add(new THREE.Mesh(new THREE.BoxGeometry(ww, 0.03, 0.07), frameMat)); // horizontal center

      // Window sill (underneath, clearly visible white ledge)
      const sill = new THREE.Mesh(new THREE.BoxGeometry(ww + 0.24, 0.07, 0.2), sillMat);
      sill.position.set(0, -wh / 2 - outerThick - 0.035, 0.08);
      g.add(sill);

      // Lintel (above, darker for contrast)
      const lintel = new THREE.Mesh(new THREE.BoxGeometry(ww + 0.2, 0.1, 0.14), concreteMat);
      lintel.position.set(0, wh / 2 + outerThick + 0.05, 0.04);
      g.add(lintel);

      // Dark reveal behind glass for depth
      const reveal = new THREE.Mesh(
        new THREE.BoxGeometry(ww + 0.1, wh + 0.1, 0.04),
        new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.9 })
      );
      reveal.position.z = -0.03;
      g.add(reveal);
    } else {
      // Side window (rotated frame) — thicker and more visible
      const sideFrameThick = 0.1;
      // Reveal / dark backplane behind glass for depth
      const reveal = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, wh + 0.14, ww + 0.14),
        new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.9 })
      );
      g.add(reveal);

      g.add(new THREE.Mesh(new THREE.BoxGeometry(sideFrameThick, wh + outerThick * 2, outerThick), frameMat).translateZ(ww / 2 + outerThick / 2));
      g.add(new THREE.Mesh(new THREE.BoxGeometry(sideFrameThick, wh + outerThick * 2, outerThick), frameMat).translateZ(-ww / 2 - outerThick / 2));
      g.add(new THREE.Mesh(new THREE.BoxGeometry(sideFrameThick, outerThick, ww + outerThick * 2), frameMat).translateY(wh / 2 + outerThick / 2));
      g.add(new THREE.Mesh(new THREE.BoxGeometry(sideFrameThick, outerThick, ww + outerThick * 2), frameMat).translateY(-wh / 2 - outerThick / 2));
      // Cross bars
      g.add(new THREE.Mesh(new THREE.BoxGeometry(0.08, wh, 0.04), frameMat));
      g.add(new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.04, ww), frameMat));
      // Sill (white ledge underneath)
      const sill = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.07, ww + 0.24), sillMat);
      sill.position.set(-0.06, -wh / 2 - outerThick - 0.035, 0);
      g.add(sill);
    }

    g.position.set(cx, cy, cz);
    scene.add(g);
    return g;
  };

  // Front large windows
  makeDetailedWindow(-1.95, 1.2, mD / 2 + 0.06, 1.2, 1.7);
  makeDetailedWindow(1.95, 1.2, mD / 2 + 0.06, 1.2, 1.7);

  // Front upper windows (smaller)
  makeDetailedWindow(-1.95, 2.8, mD / 2 + 0.06, 0.8, 0.65);
  makeDetailedWindow(1.95, 2.8, mD / 2 + 0.06, 0.8, 0.65);

  // Side windows (left wall) — pushed out 0.1 from wall center so they protrude clearly
  makeDetailedWindow(-mW / 2 - 0.1, 1.5, -0.8, 0.9, 1.2, true);
  makeDetailedWindow(-mW / 2 - 0.1, 1.5, 1.0, 0.9, 1.2, true);

  // Back windows — pushed out 0.1 from wall center
  makeDetailedWindow(-1.5, 1.4, -mD / 2 - 0.1, 1.4, 1.4);
  makeDetailedWindow(1.5, 1.4, -mD / 2 - 0.1, 1.4, 1.4);

  // ──── Door ────
  const doorMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.35, metalness: 0.3 });
  const door = new THREE.Mesh(new THREE.BoxGeometry(1.1, 2.4, 0.1), doorMat);
  door.position.set(0, 1.38, mD / 2 + 0.06); scene.add(door);

  const doorGlass = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.8, 0.04), glassMat);
  doorGlass.position.set(0, 2.05, mD / 2 + 0.12); scene.add(doorGlass);

  const handle = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 8),
    new THREE.MeshStandardMaterial({ color: 0xc47a3a, metalness: 0.85, roughness: 0.15 }));
  handle.position.set(0.38, 1.35, mD / 2 + 0.14); scene.add(handle);

  // Porch overhang
  const porchRoof = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.08, 1.0), concreteMat);
  porchRoof.position.set(0, 2.75, mD / 2 + 0.5); porchRoof.castShadow = true; scene.add(porchRoof);
  [-0.9, 0.9].forEach(x => {
    const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.57, 8), concreteMat);
    pillar.position.set(x, 1.465, mD / 2 + 0.95); scene.add(pillar);
  });
  const step = new THREE.Mesh(new THREE.BoxGeometry(2.0, 0.1, 0.6), concreteMat);
  step.position.set(0, 0.23, mD / 2 + 0.6); scene.add(step);

  // ──── Roof (in roofGroup) ────
  let leftRoofPivot: THREE.Group | null = null;
  let rightRoofPivot: THREE.Group | null = null;
  let roofBaseAngle = 0;
  let isFlat = false;

  if (roofType === 'flat') {
    isFlat = true;
    const flatRoof = new THREE.Mesh(new THREE.BoxGeometry(mW + 0.7, 0.18, mD + 0.7), roofMat);
    flatRoof.position.set(0, mH + 0.18 + 0.09, 0); flatRoof.castShadow = true;
    roofGroup.add(flatRoof);
    const edge = new THREE.Mesh(new THREE.BoxGeometry(mW + 0.7, 0.07, 0.07),
      new THREE.MeshStandardMaterial({ color: 0xc47a3a, metalness: 0.5, roughness: 0.3 }));
    edge.position.set(0, mH + 0.18 + 0.035, mD / 2 + 0.35);
    roofGroup.add(edge);
    // Flat roof: simple visibility toggle
    roofGroup.visible = !roofOpen;
  } else {
    const roofHeight = 1.6;
    const halfW = mW / 2 + 0.3; // overhang
    roofBaseAngle = Math.atan2(roofHeight, halfW);
    const slopeLen = Math.sqrt(halfW * halfW + roofHeight * roofHeight);
    const ridgeY = mH + 0.18 + roofHeight;
    const roofDepth = mD + 0.6;
    const openExtra = Math.PI / 3;
    const initT = roofOpen ? 1 : 0;

    // Left roof panel (pivot at ridge)
    leftRoofPivot = new THREE.Group();
    leftRoofPivot.position.set(0, ridgeY, 0);
    const leftPanel = new THREE.Mesh(new THREE.BoxGeometry(slopeLen, 0.08, roofDepth), roofMat);
    leftPanel.position.x = -slopeLen / 2;
    leftPanel.castShadow = true;
    leftRoofPivot.add(leftPanel);
    leftRoofPivot.rotation.z = roofBaseAngle + initT * openExtra;
    roofGroup.add(leftRoofPivot);

    // Right roof panel (pivot at ridge)
    rightRoofPivot = new THREE.Group();
    rightRoofPivot.position.set(0, ridgeY, 0);
    const rightPanel = new THREE.Mesh(new THREE.BoxGeometry(slopeLen, 0.08, roofDepth), roofMat.clone());
    rightPanel.position.x = slopeLen / 2;
    rightPanel.castShadow = true;
    rightRoofPivot.add(rightPanel);
    rightRoofPivot.rotation.z = -(roofBaseAngle + initT * openExtra);
    roofGroup.add(rightRoofPivot);

    // Ridge beam (nokbalk)
    const ridgeBeam = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.12, roofDepth),
      new THREE.MeshStandardMaterial({ color: 0x4a3520, roughness: 0.6 })
    );
    ridgeBeam.position.set(0, ridgeY + 0.04, 0);
    roofGroup.add(ridgeBeam);

    // Gable ends (driehoekige gevels voor/achter met baksteentextuur)
    const gableShape = new THREE.Shape();
    gableShape.moveTo(-mW / 2, 0);
    gableShape.lineTo(0, roofHeight);
    gableShape.lineTo(mW / 2, 0);
    gableShape.closePath();
    const gableGeo = new THREE.ExtrudeGeometry(gableShape, { depth: wallThickness, bevelEnabled: false });
    const gableBrickTex = brickTex.clone();
    gableBrickTex.repeat.set(2, 1);
    const gableMat = brickMat.clone();
    gableMat.map = gableBrickTex;

    const frontGable = new THREE.Mesh(gableGeo, gableMat);
    frontGable.position.set(0, mH + 0.18, mD / 2);
    frontGable.castShadow = true;
    scene.add(frontGable);

    const backGable = new THREE.Mesh(gableGeo, gableMat.clone());
    backGable.position.set(0, mH + 0.18, -mD / 2 - wallThickness);
    backGable.castShadow = true;
    scene.add(backGable);
  }
  scene.add(roofGroup);

  // ──── Chimney ────
  const chimneyH = roofType === 'flat' ? 0.9 : 2.0;
  const chimneyY = roofType === 'flat' ? mH + 0.18 + 0.45 : mH + 0.18 + chimneyH / 2;
  const chimBrickTex = brickTex.clone(); chimBrickTex.repeat.set(0.5, 0.5);
  const chimMat = brickMat.clone(); chimMat.map = chimBrickTex;
  const chimney = new THREE.Mesh(new THREE.BoxGeometry(0.5, chimneyH, 0.5), chimMat);
  chimney.position.set(-1.8, chimneyY, -1.2); chimney.castShadow = true;
  roofGroup.add(chimney);
  const chimCap = new THREE.Mesh(new THREE.BoxGeometry(0.65, 0.06, 0.65), concreteMat);
  chimCap.position.set(-1.8, chimneyY + chimneyH / 2 + 0.03, -1.2);
  roofGroup.add(chimCap);

  // ──── INTERIOR (visible when roof open) ────
  // Floor
  const floor = new THREE.Mesh(new THREE.BoxGeometry(mW - wallThickness * 2, 0.05, mD - wallThickness * 2), floorMat);
  floor.position.set(0, 0.205, 0);
  interiorGroup.add(floor);

  // Interior walls (room dividers)
  // Central wall dividing left/right (living vs kitchen)
  const dividerWall = new THREE.Mesh(new THREE.BoxGeometry(0.1, mH - 0.3, mD * 0.45), intWallMat);
  dividerWall.position.set(0, mH / 2 + 0.03, -mD * 0.15);
  interiorGroup.add(dividerWall);

  // Back cross wall (hallway)
  const backDivider = new THREE.Mesh(new THREE.BoxGeometry(mW * 0.6, mH - 0.3, 0.1), intWallMat);
  backDivider.position.set(-0.5, mH / 2 + 0.03, mD * 0.08);
  interiorGroup.add(backDivider);

  // ─ Furniture: Living Room (left front) ─
  // Sofa
  const sofaBase = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.35, 0.7), sofaMat);
  sofaBase.position.set(-1.8, 0.375, 1.2);
  interiorGroup.add(sofaBase);
  const sofaBack = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.4, 0.1), sofaMat);
  sofaBack.position.set(-1.8, 0.55, 0.85);
  interiorGroup.add(sofaBack);
  // Coffee table
  const coffeeTable = new THREE.Mesh(new THREE.BoxGeometry(0.8, 0.08, 0.45), tableMat);
  coffeeTable.position.set(-1.8, 0.38, 1.8);
  interiorGroup.add(coffeeTable);
  // Table legs
  [[-0.35, 1.58], [0.35, 1.58], [-0.35, 2.02], [0.35, 2.02]].forEach(([lx, lz]) => {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.16, 6), tableMat);
    leg.position.set(-1.8 + lx, 0.28, lz);
    interiorGroup.add(leg);
  });

  // ─ Furniture: Kitchen (right back) ─
  // Kitchen counter along back wall
  const counter = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.85, 0.55), kitchenMat);
  counter.position.set(1.6, 0.625, -mD / 2 + 0.45);
  interiorGroup.add(counter);
  // Counter top (lighter)
  const counterTop = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.04, 0.6),
    new THREE.MeshStandardMaterial({ color: 0xeeeeee, roughness: 0.3, metalness: 0.1 }));
  counterTop.position.set(1.6, 1.07, -mD / 2 + 0.45);
  interiorGroup.add(counterTop);

  // ─ Furniture: Dining area (right front) ─
  const diningTable = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.06, 0.8), tableMat);
  diningTable.position.set(1.6, 0.72, 0.8);
  interiorGroup.add(diningTable);
  // Chairs (simple)
  [[-0.4, 0.3], [0.4, 0.3], [-0.4, 1.3], [0.4, 1.3]].forEach(([cx, cz]) => {
    const seat = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.04, 0.3),
      new THREE.MeshStandardMaterial({ color: 0x6a5a4a, roughness: 0.7 }));
    seat.position.set(1.6 + cx, 0.42, cz);
    interiorGroup.add(seat);
    const chairBack = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.35, 0.04),
      new THREE.MeshStandardMaterial({ color: 0x6a5a4a, roughness: 0.7 }));
    chairBack.position.set(1.6 + cx, 0.59, cz - 0.13);
    interiorGroup.add(chairBack);
  });

  // ─ Bathroom hint (back left) ─
  const bathtub = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.35, 1.3),
    new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.2, metalness: 0.1 }));
  bathtub.position.set(-2.5, 0.375, -1.8);
  interiorGroup.add(bathtub);

  interiorGroup.visible = roofOpen;
  scene.add(interiorGroup);

  // ──── GARAGE ────
  const gW = 3.2, gH = 2.6, gD = 4.0;
  const gx = mW / 2 + gW / 2 - 0.08;

  makeWall(gW, gH, wallThickness, gx, gH / 2 + 0.18, -gD / 2 + mD / 2 - mD + gD, 2, 1.5, 2);
  makeWall(gW, gH, wallThickness, gx, gH / 2 + 0.18, gD / 2 + mD / 2 - mD + gD, 2, 1.5, 2);
  makeWall(wallThickness, gH, gD, gx + gW / 2, gH / 2 + 0.18, mD / 2 - mD + gD + 0, 2, 1.5, 2);

  const garageDoorMat = new THREE.MeshStandardMaterial({ color: 0x444444, roughness: 0.6, metalness: 0.3 });
  const garageDoor = new THREE.Mesh(new THREE.BoxGeometry(2.4, 2.2, 0.08), garageDoorMat);
  garageDoor.position.set(gx, 1.28, gD / 2 + mD / 2 - mD + gD + 0.06); scene.add(garageDoor);
  [0.55, 1.1, 1.65, 2.2].forEach(yy => {
    const line = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.02, 0.09),
      new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.5 }));
    line.position.set(gx, yy, gD / 2 + mD / 2 - mD + gD + 0.07); scene.add(line);
  });

  const garageRoof = new THREE.Mesh(new THREE.BoxGeometry(gW + 0.3, 0.12, gD + 0.3), roofMat);
  garageRoof.position.set(gx, gH + 0.18 + 0.06, mD / 2 - mD + gD);
  garageRoof.castShadow = true;
  roofGroup.add(garageRoof); // garage roof also in roofGroup

  // ──── GARDEN ELEMENTS (interactive) ────
  // Hedge
  const hedgeMat = new THREE.MeshStandardMaterial({ color: nightMode ? 0x1e3e1e : 0x2d6b2d, roughness: 0.9 });
  const hedge = new THREE.Mesh(new THREE.BoxGeometry(0.6, 1.0, 8), hedgeMat);
  hedge.position.set(-mW / 2 - 0.8, 0.5, 0);
  hedge.userData = { gardenItem: true, name: 'Haag', nameEn: 'Hedge', info: '~12m haag, hoogte 1m' };
  gardenGroup.add(hedge);

  // Bushes
  const bushMat = new THREE.MeshStandardMaterial({ color: nightMode ? 0x1d4a1d : 0x3a8a3a, roughness: 0.85 });
  [-0.7, 0.7].forEach((xOff, i) => {
    const bush = new THREE.Mesh(new THREE.SphereGeometry(0.35, 8, 8), bushMat);
    bush.position.set(xOff, 0.35, mD / 2 + 1.4); bush.scale.y = 0.7;
    bush.userData = { gardenItem: true, name: `Struik ${i + 1}`, nameEn: `Bush ${i + 1}`, info: 'Buxus, Ø 70cm' };
    gardenGroup.add(bush);
  });

  // Front path
  const pathMat = new THREE.MeshStandardMaterial({ color: nightMode ? 0x4a4a4a : 0xaaaaaa, roughness: 0.9 });
  const frontPath = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.02, 3.0), pathMat);
  frontPath.position.set(0, 0.01, mD / 2 + 2.5);
  frontPath.userData = { gardenItem: true, name: 'Wandelpad', nameEn: 'Walkway', info: 'Betonklinkers, 1.2m breed' };
  gardenGroup.add(frontPath);

  // Mailbox
  const mailbox = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.8, 0.15),
    new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.4 }));
  mailbox.position.set(-0.8, 0.6, mD / 2 + 3.5);
  mailbox.userData = { gardenItem: true, name: 'Brievenbus', nameEn: 'Mailbox', info: 'Aluminium, zwart' };
  gardenGroup.add(mailbox);
  const mailSlot = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.04, 0.16),
    new THREE.MeshStandardMaterial({ color: 0xc47a3a, metalness: 0.7, roughness: 0.2 }));
  mailSlot.position.set(-0.8, 0.85, mD / 2 + 3.5);
  gardenGroup.add(mailSlot);

  // Flower bed (new)
  const flowerBed = new THREE.Mesh(new THREE.BoxGeometry(2.5, 0.15, 0.8),
    new THREE.MeshStandardMaterial({ color: nightMode ? 0x2a1a0a : 0x5a3a1a, roughness: 1.0 }));
  flowerBed.position.set(2.5, 0.075, mD / 2 + 1.8);
  flowerBed.userData = { gardenItem: true, name: 'Bloembed', nameEn: 'Flower bed', info: 'Tuinaarde, 2.5m × 0.8m' };
  gardenGroup.add(flowerBed);
  // Flowers
  [0, 0.5, 1.0, 1.5, 2.0].forEach(off => {
    const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.35, 6),
      new THREE.MeshStandardMaterial({ color: 0x228b22 }));
    stem.position.set(1.5 + off, 0.325, mD / 2 + 1.8);
    gardenGroup.add(stem);
    const flower = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 6),
      new THREE.MeshStandardMaterial({ color: [0xff6b6b, 0xffd93d, 0xff6bff, 0xff8c42, 0x6bcbff][Math.floor(Math.random() * 5)] }));
    flower.position.set(1.5 + off, 0.52, mD / 2 + 1.8);
    gardenGroup.add(flower);
  });

  // Tree (new)
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.15, 1.8, 8),
    new THREE.MeshStandardMaterial({ color: 0x5a3a1a, roughness: 0.9 }));
  trunk.position.set(-4.5, 0.9, -1.5);
  trunk.userData = { gardenItem: true, name: 'Boom', nameEn: 'Tree', info: 'Sierappel, hoogte ~3m' };
  gardenGroup.add(trunk);
  const canopy = new THREE.Mesh(new THREE.SphereGeometry(1.0, 10, 10),
    new THREE.MeshStandardMaterial({ color: nightMode ? 0x1a3a1a : 0x2a7a2a, roughness: 0.9 }));
  canopy.position.set(-4.5, 2.3, -1.5);
  canopy.userData = { gardenItem: true, name: 'Boom', nameEn: 'Tree', info: 'Sierappel, hoogte ~3m' };
  gardenGroup.add(canopy);

  scene.add(gardenGroup);

  // ──── Night details ────
  if (nightMode) {
    const starGeo = new THREE.BufferGeometry();
    const starPositions: number[] = [];
    for (let i = 0; i < 80; i++) {
      starPositions.push((Math.random() - 0.5) * 30, 8 + Math.random() * 8, (Math.random() - 0.5) * 30);
    }
    starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starPositions, 3));
    const starMat2 = new THREE.PointsMaterial({ color: 0xffffff, size: 0.08, sizeAttenuation: true });
    scene.add(new THREE.Points(starGeo, starMat2));

    const bulbGeo = new THREE.SphereGeometry(0.06, 8, 8);
    const bulbMat = new THREE.MeshStandardMaterial({ color: 0xffcc44, emissive: 0xffaa22, emissiveIntensity: 2 });
    const bulb = new THREE.Mesh(bulbGeo, bulbMat);
    bulb.position.set(0, 2.6, mD / 2 + 0.5); scene.add(bulb);

    // Garden path lights
    [-1, 1, 3].forEach(zOff => {
      const pathLight = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.5, 6),
        new THREE.MeshStandardMaterial({ color: 0x333333, roughness: 0.5 }));
      pathLight.position.set(0.8, 0.25, mD / 2 + zOff);
      scene.add(pathLight);
      const pathBulb = new THREE.Mesh(new THREE.SphereGeometry(0.04, 6, 6),
        new THREE.MeshStandardMaterial({ color: 0xffeebb, emissive: 0xffcc88, emissiveIntensity: 1.5 }));
      pathBulb.position.set(0.8, 0.52, mD / 2 + zOff);
      scene.add(pathBulb);
      const pl = new THREE.PointLight(0xffddaa, 0.3, 2);
      pl.position.set(0.8, 0.52, mD / 2 + zOff);
      lightsGroup.add(pl);
    });
  }

  return { nummerSprites, roofGroup, interiorGroup, gardenGroup, lightsGroup, leftRoofPivot, rightRoofPivot, roofBaseAngle, isFlat };
}

// Material calculation helper
function calculateMaterials(mW: number, mH: number, mD: number, gW: number, gH: number, gD: number) {
  // Main building walls
  const mainFront = mW * mH;
  const mainBack = mW * mH;
  const mainLeft = mD * mH;
  const mainRight = mD * mH * 0.5; // partial due to garage
  const mainTotal = mainFront + mainBack + mainLeft + mainRight;

  // Garage walls
  const garageTotal = (gW * gH * 2) + (gD * gH); // front, back, side

  // Window openings to subtract
  const frontLargeWin = 1.2 * 1.7 * 2;
  const frontSmallWin = 0.8 * 0.65 * 2;
  const sideWin = 0.9 * 1.1 * 2;
  const backWin = 1.3 * 1.3 * 2;
  const doorOpening = 1.1 * 2.4;
  const garageDoorOpening = 2.4 * 2.2;
  const totalOpenings = frontLargeWin + frontSmallWin + sideWin + backWin + doorOpening + garageDoorOpening;

  const grossM2 = mainTotal + garageTotal;
  const netM2 = grossM2 - totalOpenings;

  return {
    grossM2: Math.round(grossM2 * 10) / 10,
    netM2: Math.round(netM2 * 10) / 10,
    openingsM2: Math.round(totalOpenings * 10) / 10,
  };
}

export default function HousePreview3D({ brickColor, labels, calcData, stenen }: HousePreview3DProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<ReturnType<typeof createOrbitControls> | null>(null);
  const animIdRef = useRef<number>(0);
  const groupsRef = useRef<HouseGroups | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const roofAnimRef = useRef(0); // current animation progress 0-1
  const roofTargetRef = useRef(0); // target: 0=closed, 1=open

  const [roofType, setRoofType] = useState<string>('tiles');
  const [nightMode, setNightMode] = useState(false);
  const [roofOpen, setRoofOpen] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gardenTooltip, setGardenTooltip] = useState<{ x: number; y: number; name: string; info: string } | null>(null);
  const [show360Badge, setShow360Badge] = useState(true);
  const show360BadgeRef = useRef(true);
  const isUserInteractingRef = useRef(false); // true while pointer/touch is down
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const autoRotatingRef = useRef(true); // continuously rotate when not interacting

  // PDF m² modal state
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfM2, setPdfM2] = useState<string>('');

  const materials = useMemo(() => calculateMaterials(6.8, 3.2, 5.4, 3.2, 2.6, 4.0), []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    // Skip init if container is hidden (prevents WebGL crash on zero-width elements)
    if (container.offsetWidth === 0) return;

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelays = [150, 600, 1500]; // ms — first delay gives Safari time to settle
    let lastFailReason = ''; // captured for diagnostics in the error UI

    function scheduleRetry() {
      if (retryCount < maxRetries) {
        const delay = retryDelays[retryCount];
        retryCount++;
        setTimeout(attemptInit, delay);
        return true;
      }
      return false;
    }

    function attemptInit() {
      if (cancelled) return;
      if (container!.offsetWidth === 0) {
        // Container not yet visible, retry
        scheduleRetry();
        return;
      }
      initWebGL();
    }

    // First attempt after 1 frame
    const initTimeout = requestAnimationFrame(() => {
      if (cancelled) return;
      attemptInit();
    });

    function initWebGL() {
      const w = Math.max(container!.clientWidth, 300);
      const h = Math.max(container!.clientHeight, 420);

      let renderer: THREE.WebGLRenderer | null = null;

      try {
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(38, w / h, 0.1, 100);
        cameraRef.current = camera;

        // Safari-friendly renderer options:
        //  - 'low-power' avoids forcing the discrete GPU on multi-GPU Macs (more stable)
        //  - failIfMajorPerformanceCaveat:false lets software fallback succeed if needed
        //  - antialias disabled on Safari to reduce GPU memory pressure
        const rendererParams: THREE.WebGLRendererParameters = {
          antialias: !isSafari,
          alpha: true,
          powerPreference: 'low-power',
          failIfMajorPerformanceCaveat: false,
          preserveDrawingBuffer: true, // needed for toDataURL() PDF export
          stencil: false,
          depth: true,
        };

        try {
          renderer = new THREE.WebGLRenderer(rendererParams);
        } catch (err) {
          lastFailReason = `constructor threw: ${(err as Error)?.message || String(err)}`;
          // Last-ditch: drop antialias if it wasn't already off
          try {
            renderer = new THREE.WebGLRenderer({ ...rendererParams, antialias: false });
          } catch (err2) {
            lastFailReason = `constructor threw twice: ${(err2 as Error)?.message || String(err2)}`;
            if (scheduleRetry()) return;
            setError(`WebGL niet beschikbaar (${lastFailReason})`);
            return;
          }
        }

        // Three.js may construct a renderer whose context is null/lost on Safari.
        // Verify the GL context is live before continuing.
        const gl = renderer.getContext();
        if (!gl) {
          lastFailReason = 'getContext() returned null';
          renderer.dispose();
          renderer = null;
          if (scheduleRetry()) return;
          setError(`WebGL niet beschikbaar (${lastFailReason})`);
          return;
        }
        if (gl.isContextLost()) {
          lastFailReason = 'context was lost on creation';
          renderer.dispose();
          renderer = null;
          if (scheduleRetry()) return;
          setError(`WebGL niet beschikbaar (${lastFailReason})`);
          return;
        }
        // Surface GL renderer info for debugging (Safari often falls back to software rasterizer)
        const dbg = gl.getExtension('WEBGL_debug_renderer_info');
        if (dbg) {
          const vendor = gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL);
          const glRenderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
          console.info('HousePreview3D GL:', { vendor, renderer: glRenderer, isSafari });
        }

        renderer.setSize(w, h);
        // Cap pixel ratio more aggressively on Safari to keep within memory limits
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, isSafari ? 1.5 : 2));
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.1;
        container.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Bind to a non-null local so the rest of the closure has clean typing.
        const r = renderer;

        const controls = createOrbitControls(camera, r.domElement,
          // onInteractStart: stop auto-rotation and cancel resume timer
          () => {
            isUserInteractingRef.current = true;
            autoRotatingRef.current = false;
            if (resumeTimerRef.current) {
              clearTimeout(resumeTimerRef.current);
              resumeTimerRef.current = null;
            }
            // Hide 360° badge on first interaction
            if (show360BadgeRef.current) {
              show360BadgeRef.current = false;
              setShow360Badge(false);
            }
          },
          // onInteractEnd: resume auto-rotation after 3 seconds of no interaction
          () => {
            isUserInteractingRef.current = false;
            if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
            resumeTimerRef.current = setTimeout(() => {
              autoRotatingRef.current = true;
            }, 3000);
          }
        );
        controlsRef.current = controls;

        groupsRef.current = buildHouse(scene, brickColor, roofType, nightMode, roofOpen, stenen);

        // Continuous slow auto-rotation: full 360° in ~35 seconds
        // v5 blok 3: trager draaien (Filip) — één volledige rotatie per 70s
        // i.p.v. 35s. Klikken/slepen blijft gewoon werken (drag onderbreekt).
        const AUTO_ROTATE_SPEED = (Math.PI * 2) / 70; // radians per second
        let lastTime = performance.now();

        const animate = () => {
          animIdRef.current = requestAnimationFrame(animate);

          // Skip rendering if context was lost — the restore handler will resume it.
          const ctx = r.getContext();
          if (!ctx || ctx.isContextLost()) return;

          const now = performance.now();
          const delta = (now - lastTime) / 1000;
          lastTime = now;

          if (autoRotatingRef.current && !isUserInteractingRef.current) {
            controls.autoRotateTick(AUTO_ROTATE_SPEED * delta);
          }

          // Animated 3D roof open/close (pitched roof only)
          const g = groupsRef.current;
          if (g && g.leftRoofPivot && g.rightRoofPivot && g.roofBaseAngle > 0) {
            const target = roofTargetRef.current;
            const cur = roofAnimRef.current;
            const diff = target - cur;
            if (Math.abs(diff) > 0.001) {
              roofAnimRef.current += diff * 0.07;
              const t = roofAnimRef.current;
              const extra = Math.PI / 3;
              g.leftRoofPivot.rotation.z = g.roofBaseAngle + t * extra;
              g.rightRoofPivot.rotation.z = -(g.roofBaseAngle + t * extra);
              g.interiorGroup.visible = t > 0.25;
            }
          }

          r.render(scene, camera);
        };
        animate();

        // Recover gracefully when Safari drops the context (tab switch, GPU pressure).
        const canvas = r.domElement;
        const onContextLost = (ev: Event) => {
          ev.preventDefault(); // signals the browser to fire 'restored' later
          cancelAnimationFrame(animIdRef.current);
          console.warn('HousePreview3D: WebGL context lost — waiting for restore');
        };
        const onContextRestored = () => {
          if (cancelled || !sceneRef.current) return;
          console.info('HousePreview3D: WebGL context restored — rebuilding scene');
          // Three.js textures/buffers were lost with the context, so rebuild.
          groupsRef.current = buildHouse(sceneRef.current, brickColor, roofType, nightMode, roofOpen, stenen);
          lastTime = performance.now();
          animIdRef.current = requestAnimationFrame(animate);
        };
        canvas.addEventListener('webglcontextlost', onContextLost as EventListener, false);
        canvas.addEventListener('webglcontextrestored', onContextRestored as EventListener, false);

        const handleResize = () => {
          const nw = container.clientWidth;
          const nh = Math.max(container.clientHeight, 420);
          camera.aspect = nw / nh;
          camera.updateProjectionMatrix();
          r.setSize(nw, nh);
        };
        window.addEventListener('resize', handleResize);

        cleanupRef.current = () => {
          window.removeEventListener('resize', handleResize);
          cancelAnimationFrame(animIdRef.current);
          if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
          canvas.removeEventListener('webglcontextlost', onContextLost as EventListener);
          canvas.removeEventListener('webglcontextrestored', onContextRestored as EventListener);
          controls.dispose();
          // Force-release the GL context so Safari recovers its slot immediately
          // instead of waiting for GC. Critical when the component unmounts/remounts.
          const loseExt = r.getContext()?.getExtension('WEBGL_lose_context');
          loseExt?.loseContext();
          r.dispose();
          if (container!.contains(r.domElement)) container!.removeChild(r.domElement);
        };
      } catch (e) {
        // Clean up partial state so the next retry starts fresh.
        if (renderer) {
          if (container!.contains(renderer.domElement)) container!.removeChild(renderer.domElement);
          renderer.dispose();
          rendererRef.current = null;
        }
        lastFailReason = `init threw: ${(e as Error)?.message || String(e)}`;
        console.error('HousePreview3D init error:', e);
        if (scheduleRetry()) return;
        setError(`WebGL niet beschikbaar (${lastFailReason})`);
      }
    } // end initWebGL

    return () => {
      cancelled = true;
      cancelAnimationFrame(initTimeout);
      cleanupRef.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rebuild house when visual props change (NOT roofOpen — that's handled separately)
  useEffect(() => {
    if (sceneRef.current) {
      groupsRef.current = buildHouse(sceneRef.current, brickColor, roofType, nightMode, roofOpen, stenen);
      // Sync animation ref with current state after rebuild
      roofAnimRef.current = roofOpen ? 1 : 0;
      roofTargetRef.current = roofOpen ? 1 : 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brickColor, roofType, nightMode, JSON.stringify(stenen ?? null)]);

  // Handle roof open/close (flat=visibility, pitched=animation)
  useEffect(() => {
    const g = groupsRef.current;
    if (!g) return;
    if (g.isFlat) {
      g.roofGroup.visible = !roofOpen;
      g.interiorGroup.visible = roofOpen;
    } else {
      roofTargetRef.current = roofOpen ? 1 : 0;
    }
  }, [roofOpen]);

  // Garden click handler (raycasting)
  const handleCanvasClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    const camera = cameraRef.current;
    const groups = groupsRef.current;
    if (!container || !camera || !groups) return;

    const canvas = rendererRef.current?.domElement;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    mouseRef.current.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

    raycasterRef.current.setFromCamera(mouseRef.current, camera);

    // V6 (vraag Franky): klik op een nummerbolletje -> draai naar die gevel.
    // Volgorde camera-theta: 0 = rechts, pi/2 = voorgevel, pi = links, -pi/2 = achter.
    if (groups.nummerSprites.length > 0) {
      const nrHits = raycasterRef.current.intersectObjects(groups.nummerSprites, false);
      if (nrHits.length > 0) {
        const vlak = nrHits[0].object.userData.gevelVlak as number;
        const thetas = [Math.PI / 2, Math.PI, 0, -Math.PI / 2];
        controlsRef.current?.draaiNaar?.(thetas[vlak] ?? Math.PI / 2);
        return;
      }
    }

    const intersects = raycasterRef.current.intersectObjects(groups.gardenGroup.children, true);

    if (intersects.length > 0) {
      let obj = intersects[0].object;
      // Walk up to find userData
      while (obj && !obj.userData?.gardenItem && obj.parent) {
        obj = obj.parent as THREE.Object3D;
      }
      if (obj?.userData?.gardenItem) {
        setGardenTooltip({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          name: obj.userData.name,
          info: obj.userData.info,
        });
        setTimeout(() => setGardenTooltip(null), 3000);
      } else {
        setGardenTooltip(null);
      }
    } else {
      setGardenTooltip(null);
    }
  }, []);

  const handleDownload = useCallback((m2Input: string) => {
    if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;
    rendererRef.current.render(sceneRef.current, cameraRef.current);
    const dataUrl = rendererRef.current.domElement.toDataURL('image/png');
    const dateStr = new Date().toLocaleDateString('nl-BE', { year: 'numeric', month: 'long', day: 'numeric' });
    const refNr = `OFF-${Date.now().toString(36).toUpperCase()}`;

    const roofLabel = roofType === 'flat' ? 'Plat dak' : 'Dakpannen';

    // m² indicative price section
    const m2Val = parseFloat(m2Input);
    let m2Section = '';
    if (calcData && !isNaN(m2Val) && m2Val > 0) {
      const indicativeTotal = calcData.pricePerM2 * m2Val;
      m2Section = `
        <div class="section">
          <h2>Indicatieve prijsschatting</h2>
          <table class="mat-table">
            <tr><td class="label">Opgegeven oppervlakte</td><td class="val">${m2Val} m²</td></tr>
            <tr><td class="label">Prijs per m²</td><td class="val">€${calcData.pricePerM2.toFixed(2)}</td></tr>
            <tr><td class="label" style="font-weight:700">Indicatieve prijs (excl. BTW)</td><td class="val highlight">€${Math.round(indicativeTotal).toLocaleString('nl-BE')}</td></tr>
            <tr><td class="label">Indicatieve prijs (incl. 21% BTW)</td><td class="val">€${Math.round(indicativeTotal * 1.21).toLocaleString('nl-BE')}</td></tr>
          </table>
          <div class="disclaimer">
            <p><strong>Dit is een indicatieve prijsschatting en geen officiële offerte.</strong></p>
            <p>Contacteer ons voor een exacte prijsopgave op maat.</p>
          </div>
        </div>`;
    } else if (calcData) {
      m2Section = `
        <div class="section">
          <h2>Indicatieve prijsschatting</h2>
          <table class="mat-table">
            <tr><td class="label">Oppervlakte</td><td class="val">— m²</td></tr>
            <tr><td class="label">Prijs per m²</td><td class="val">€${calcData.pricePerM2.toFixed(2)}</td></tr>
          </table>
          <div class="disclaimer">
            <p><strong>Dit is een indicatieve prijsschatting en geen officiële offerte.</strong></p>
            <p>Contacteer ons voor een exacte prijsopgave op maat.</p>
          </div>
        </div>`;
    }

    // Build quote rows if calcData is provided
    let quoteRows = '';
    let quoteSection = '';
    if (calcData) {
      const rows: string[] = [];
      rows.push(`<tr><td>Baksteenstrips — ${calcData.productName}</td><td class="r">${calcData.totalStones} stuks (${calcData.stonesPerM2}/m²)</td><td class="r">${calcData.area} m²</td><td class="r">€${calcData.pricePerM2.toFixed(2)}</td><td class="r price">€${Math.round(calcData.stonePrice).toLocaleString('nl-BE')}</td></tr>`);
      if (calcData.totalCornerPieces > 0) {
        rows.push(`<tr><td>Hoekstenen</td><td class="r">${calcData.totalCornerPieces} stuks</td><td class="r">${calcData.cornerCount} hoeken × ${calcData.cornerLength}m</td><td class="r">—</td><td class="r price">€${Math.round(calcData.cornerPrice).toLocaleString('nl-BE')}</td></tr>`);
      }
      if (calcData.includeGlue) {
        rows.push(`<tr><td>Lijm</td><td class="r">${calcData.glueKg} kg</td><td class="r">${calcData.glueBags} zakken (25kg)</td><td class="r">€18,00/zak</td><td class="r price">€${calcData.gluePrice.toLocaleString('nl-BE')}</td></tr>`);
      }
      if (calcData.includeGrout) {
        rows.push(`<tr><td>Voegsel (${calcData.groutColor})</td><td class="r">${calcData.groutKg} kg</td><td class="r">${calcData.groutBags} zakken (25kg)</td><td class="r">€22,00/zak</td><td class="r price">€${calcData.groutPrice.toLocaleString('nl-BE')}</td></tr>`);
      }
      quoteRows = rows.join('');

      quoteSection = `
        <div class="section">
          <h2>Offerte</h2>
          <table class="quote-table">
            <thead>
              <tr><th>Omschrijving</th><th class="r">Hoeveelheid</th><th class="r">Detail</th><th class="r">Eenheidsprijs</th><th class="r">Totaal</th></tr>
            </thead>
            <tbody>
              ${quoteRows}
            </tbody>
            <tfoot>
              <tr class="subtotal"><td colspan="4">Subtotaal excl. BTW</td><td class="r price">€${Math.round(calcData.totalPrice).toLocaleString('nl-BE')}</td></tr>
              <tr><td colspan="4">BTW 21%</td><td class="r price">€${Math.round(calcData.totalPrice * 0.21).toLocaleString('nl-BE')}</td></tr>
              <tr class="total"><td colspan="4">Totaal incl. BTW</td><td class="r price">€${Math.round(calcData.totalPrice * 1.21).toLocaleString('nl-BE')}</td></tr>
            </tfoot>
          </table>
        </div>

        <div class="section params">
          <h2>Berekeningsparameters</h2>
          <div class="grid2">
            <div class="param"><span class="label">Oppervlakte</span><span class="val">${calcData.area} m²</span></div>
            <div class="param"><span class="label">Voegbreedte</span><span class="val">${calcData.jointWidth} mm</span></div>
            <div class="param"><span class="label">Snijverlies</span><span class="val">${calcData.cuttingLoss}%</span></div>
            <div class="param"><span class="label">Hoeken</span><span class="val">${calcData.cornerCount} × ${calcData.cornerLength}m</span></div>
          </div>
        </div>`;
    }

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(`<!DOCTYPE html><html><head><title>Gevelsteenbedrijf — Offerte ${refNr}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:'DM Sans','Segoe UI',system-ui,sans-serif;margin:0;padding:0;background:#fff;color:#1a1a1a;font-size:13px;line-height:1.5}
        .page{max-width:800px;margin:0 auto;padding:40px 48px}
        .hdr{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #c47a3a;padding-bottom:18px;margin-bottom:28px}
        .hdr h1{font-size:22px;font-weight:800;letter-spacing:-0.5px}.hdr .sub{color:#c47a3a;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px}
        .hdr .ref{text-align:right;font-size:11px;color:#8a7e74}.hdr .ref strong{display:block;font-size:13px;color:#1a1a1a}
        .section{margin-bottom:24px}
        .section h2{font-size:15px;font-weight:700;margin-bottom:12px;padding-bottom:6px;border-bottom:1px solid #eee;display:flex;align-items:center;gap:6px}
        .preview-img{width:100%;max-height:300px;object-fit:contain;border-radius:8px;border:1px solid #eee;margin-bottom:8px}
        .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
        .info-box{background:#faf7f3;border-radius:6px;padding:12px 14px}
        .info-box .label{font-size:10px;text-transform:uppercase;letter-spacing:0.8px;color:#8a7e74;font-weight:600}
        .info-box .val{display:block;font-size:15px;font-weight:700;color:#1a1a1a;margin-top:2px}
        .color-dot{display:inline-block;width:14px;height:14px;border-radius:50%;border:2px solid #ddd;vertical-align:middle;margin-right:6px}
        .quote-table{width:100%;border-collapse:collapse;font-size:12px}
        .quote-table th{background:#1a1a1a;color:#fff;padding:8px 10px;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.5px}
        .quote-table td{padding:8px 10px;border-bottom:1px solid #f0f0f0}
        .quote-table .r{text-align:right}
        .quote-table .price{font-weight:700;color:#c47a3a;white-space:nowrap}
        .quote-table tfoot td{font-weight:600;border-top:1px solid #ddd;padding-top:10px}
        .quote-table tfoot .subtotal td{border-top:2px solid #1a1a1a}
        .quote-table tfoot .total td{font-size:15px;font-weight:800;color:#c47a3a;border-top:2px solid #c47a3a;padding-top:12px}
        .mat-table{width:100%;font-size:12px}
        .mat-table td{padding:5px 0}.mat-table .label{color:#8a7e74}.mat-table .val{text-align:right;font-weight:600}
        .mat-table .highlight{color:#c47a3a;font-weight:700;font-size:14px}
        .grid2{display:grid;grid-template-columns:1fr 1fr;gap:8px}
        .params .param{background:#f5f3f0;border-radius:6px;padding:8px 12px;display:flex;justify-content:space-between}
        .params .param .label{color:#8a7e74;font-size:11px}.params .param .val{font-weight:700;font-size:12px}
        .disclaimer{margin-top:10px;background:#fff8f3;border:1px solid #c47a3a33;border-left:3px solid #c47a3a;border-radius:4px;padding:10px 14px;font-size:11px;color:#6a4a2a;line-height:1.6}
        .disclaimer strong{display:block;margin-bottom:2px;color:#c47a3a}
        .ft{margin-top:32px;font-size:10px;color:#8a7e74;border-top:2px solid #eee;padding-top:14px;text-align:center;line-height:1.6}
        .ft strong{color:#1a1a1a}
        .badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px}
        .badge-accent{background:#c47a3a;color:#fff}
        @media print{
          body{padding:0;font-size:11px}
          .page{padding:20px 28px;max-width:100%}
          .preview-img{max-height:240px}
          .section{page-break-inside:avoid}
          .quote-table{font-size:10px}
        }
      </style></head><body>
      <div class="page">
        <div class="hdr">
          <div>
            <h1>Gevelsteenbedrijf.be</h1>
            <span class="sub">3D Gevelpreview & Offerte</span>
          </div>
          <div class="ref">
            <strong>${refNr}</strong>
            ${dateStr}
          </div>
        </div>

        <div class="section">
          <h2>3D Preview</h2>
          <img src="${dataUrl}" alt="3D Preview" class="preview-img" />
          <div class="info-grid" style="margin-top:12px">
            <div class="info-box">
              <span class="label">Gekozen steen</span>
              <span class="val"><span class="color-dot" style="background:${brickColor}"></span>${calcData?.productName || 'Baksteenstrip'}</span>
            </div>
            <div class="info-box">
              <span class="label">Kleur / Textuur</span>
              <span class="val">${calcData?.productColor || '—'} / ${calcData?.productTexture || '—'}</span>
            </div>
            <div class="info-box">
              <span class="label">Daktype</span>
              <span class="val">${roofLabel}</span>
            </div>
            <div class="info-box">
              <span class="label">Prijs per m²</span>
              <span class="val">${calcData ? '€' + calcData.pricePerM2.toFixed(2) + '/m²' : '—'}</span>
            </div>
          </div>
        </div>

        <div class="section">
          <h2>Materiaalberekening 3D Model</h2>
          <table class="mat-table">
            <tr><td class="label">Bruto geveloppervlak</td><td class="val">${materials.grossM2} m²</td></tr>
            <tr><td class="label">Openingen (ramen, deuren, garage)</td><td class="val" style="color:#cc4444">-${materials.openingsM2} m²</td></tr>
            <tr><td class="label" style="font-weight:700">Netto baksteenoppervlak</td><td class="val highlight">${materials.netM2} m²</td></tr>
            <tr><td class="label">Geschatte strips (65×210mm)</td><td class="val">${Math.ceil(materials.netM2 * 52)} stuks</td></tr>
          </table>
        </div>

        ${m2Section}

        ${quoteSection}

        <div class="ft">
          <p>Dit is een digitale preview en indicatieve berekening van uw gevelkeuze.<br/>
          De werkelijke kleur, textuur en hoeveelheden kunnen licht afwijken. Prijzen zijn indicatief.</p>
          <p style="margin-top:4px;font-weight:600;color:#c47a3a">Dit is een indicatieve prijsschatting en geen officiële offerte. Contacteer ons voor een exacte prijsopgave op maat.</p>
          <p style="margin-top:8px"><strong>Gevelsteenbedrijf.be</strong> — Gegenereerd op ${dateStr}</p>
          <p style="margin-top:4px;font-size:9px">Referentie: ${refNr} | Geldigheid: 30 dagen | Alle prijzen excl. plaatsing & levering</p>
        </div>
      </div>
      <script>setTimeout(()=>window.print(),600)<\\/script></body></html>`);
      win.document.close();
    }
  }, [brickColor, roofType, calcData, materials]);

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 text-center border border-gray-100">
        <span className="text-4xl block mb-3"></span>
        <p className="text-sm text-[#8a7e74]">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 h-full flex flex-col">
      {/* PDF m² modal */}
      {showPdfModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setShowPdfModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-80 mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-1">PDF downloaden</h3>
            <p className="text-xs text-[#8a7e74] mb-4">Voer het aantal m² in voor een indicatieve prijsschatting (optioneel).</p>
            <label className="block text-xs font-semibold text-[#4a4a4a] mb-1">Aantal m²</label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={pdfM2}
              onChange={e => setPdfM2(e.target.value)}
              placeholder="bijv. 85"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c47a3a]/40 focus:border-[#c47a3a] mb-3"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter') { setShowPdfModal(false); handleDownload(pdfM2); } }}
            />
            <p className="text-[10px] text-[#8a7e74] italic mb-4">Dit is een indicatieve prijsschatting en geen officiële offerte. Contacteer ons voor een exacte prijsopgave op maat.</p>
            <div className="flex gap-2">
              <button onClick={() => setShowPdfModal(false)} className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-xs font-medium text-[#4a4a4a] hover:bg-gray-50 transition-colors">Annuleren</button>
              <button onClick={() => { setShowPdfModal(false); handleDownload(pdfM2); }} className="flex-1 px-3 py-2 rounded-lg bg-[#c47a3a] text-white text-xs font-semibold hover:bg-[#b06a2a] transition-colors">PDF genereren</button>
            </div>
          </div>
        </div>
      )}

      {/* 3D Canvas + overlays — v3.2.79: w-full + h-full zodat de canvas
           de hele wrapper-hoogte vult. Anders kreeg je een witte balk
           onder het huis wanneer de parent flex-1 hoger was dan 420px.
           Filip: 'er zit een witte balk onder het huis dat was niet zo
           ervoor'. */}
      <div className="relative flex-1 min-h-[420px]">
        <div ref={containerRef} className="w-full h-full" style={{ minHeight: 420, touchAction: 'none' }}
          onClick={handleCanvasClick} />

        {/* Controls overlay - top of canvas */}
        <div className="absolute top-2 left-2 right-2 flex items-center gap-1 z-10">
          {(['flat', 'tiles'] as const).map(rt => (
            <button key={rt} onClick={() => setRoofType(rt)}
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium backdrop-blur-sm transition-all ${roofType === rt ? 'bg-[#c47a3a] text-white shadow-sm' : 'bg-white/80 text-[#4a4a4a] hover:bg-white'}`}>
              {rt === 'flat' ? labels.roofFlat : labels.roofTiles}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => setNightMode(!nightMode)}
              className={`px-1.5 py-0.5 rounded text-[10px] font-medium backdrop-blur-sm transition-all ${nightMode ? 'bg-[#1a2a3d] text-yellow-300' : 'bg-white/80 text-[#4a4a4a] hover:bg-white'}`}>
              {nightMode ? (labels.nightMode || 'Nacht') : (labels.dayMode || 'Dag')}
            </button>
            <button onClick={() => { setPdfM2(''); setShowPdfModal(true); }}
              className="flex items-center gap-0.5 px-1.5 py-0.5 bg-[#c47a3a]/90 text-white text-[10px] font-medium rounded backdrop-blur-sm hover:bg-[#c47a3a] transition-colors">
              <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" />
              </svg>
              PDF
            </button>
          </div>
        </div>

        {/* Garden tooltip */}
        {gardenTooltip && (
          <div ref={tooltipRef}
            className="absolute pointer-events-none bg-[#1a1a1a]/90 text-white px-3 py-2 rounded-lg text-xs shadow-lg backdrop-blur-sm z-10 transition-opacity"
            style={{ left: Math.min(gardenTooltip.x, 280), top: Math.max(gardenTooltip.y - 55, 8) }}>
            <p className="font-bold text-[#c47a3a]">{gardenTooltip.name}</p>
            <p className="text-gray-300 mt-0.5">{gardenTooltip.info}</p>
          </div>
        )}

        {/* Material calculation overlay */}
        {showMaterials && (
          <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-4 text-xs z-10 border border-gray-200 w-56">
            <h4 className="font-bold text-[#1a1a1a] mb-2 flex items-center gap-1.5">
              {labels.materialsLabel || 'Materiaalberekening'}
            </h4>
            <div className="space-y-1.5">
              <div className="flex justify-between">
                <span className="text-[#8a7e74]">{labels.materialsWall || 'Totale gevelopp.'}</span>
                <span className="font-semibold text-[#1a1a1a]">{materials.grossM2} m²</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#8a7e74]">{labels.materialsWindows || 'Openingen'}</span>
                <span className="font-semibold text-red-500">-{materials.openingsM2} m²</span>
              </div>
              <div className="border-t border-gray-200 pt-1.5 flex justify-between">
                <span className="font-bold text-[#1a1a1a]">{labels.materialsNet || 'Netto bakstenen'}</span>
                <span className="font-bold text-[#c47a3a]">{materials.netM2} m²</span>
              </div>
              <div className="flex justify-between text-[10px] text-[#8a7e74]">
                <span>± strips (65×210mm)</span>
                <span className="font-semibold">{Math.ceil(materials.netM2 * 52)} stuks</span>
              </div>
            </div>
          </div>
        )}

        {/* Tuinhint verwijderd op vraag Franky (19/8) — de klik-info op
             tuinelementen blijft werken, maar zonder permanent label. */}

        {/* 360° interactive badge — disappears after first drag */}
        {show360Badge && (
          <div className="absolute top-10 right-2 flex flex-col items-center gap-0.5 pointer-events-none z-10 transition-opacity duration-500">
            <div className="flex items-center gap-1 bg-black/50 backdrop-blur-sm text-white px-2 py-1 rounded-full shadow-md">
              <svg className="w-3.5 h-3.5 animate-spin-slow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                <polyline points="21 3 21 9 15 9" />
              </svg>
              <span className="text-[10px] font-semibold tracking-wide">360°</span>
            </div>
            <span className="text-[9px] text-white/70 bg-black/30 backdrop-blur-sm px-1.5 py-0.5 rounded">{labels.dragHint || 'slepen'}</span>
          </div>
        )}
      </div>

    </div>
  );
}
